import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaUser, FaPhone, FaMapMarkerAlt, FaCity, FaEnvelope, FaArrowRight, FaTimes, FaSpinner } from 'react-icons/fa';
import '../styles/ShippingForm.css';

const ShippingForm = ({ pack, user, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    full_name: user?.name || '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'España'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [validating, setValidating] = useState(false);
  const [addressValid, setAddressValid] = useState(null);
  
  const debounceTimer = useRef(null);

  // Validar formato de teléfono español (móvil o fijo)
  const isValidPhone = (phone) => {
    const cleaned = phone.replace(/[\s\-\.]/g, '');
    return /^(\+34)?[679]\d{8}$/.test(cleaned);
  };

  // Validar código postal español (5 dígitos)
  const isValidPostalCode = (code) => {
    return /^\d{5}$/.test(code.trim());
  };

  // Función que llama a la API para validar dirección
  const validateAddress = async (city, postal_code, country) => {
    if (!city.trim() || !postal_code.trim() || !country.trim()) {
      setAddressValid(null);
      return;
    }
    
    if (!isValidPostalCode(postal_code)) {
      setAddressValid(null);
      return;
    }
    
    setValidating(true);
    setAddressValid(null);
    
    try {
      const validationResponse = await axios.post(
        'https://reanimeitor.com/backend/validate_address.php',
        { city, postal_code, country },
        { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
      );
      
      if (validationResponse.data.success && validationResponse.data.valid) {
        setAddressValid(true);
        setError('');
      } else {
        setAddressValid(false);
      }
    } catch (err) {
      console.error('Error validando dirección:', err);
      setAddressValid(null);
    } finally {
      setValidating(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Limpiar error del campo al cambiarlo
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
    setError('');

    // Validar teléfono mientras escribe (máx 12 caracteres con prefijo)
    if (name === 'phone') {
      const cleaned = value.replace(/[\s\-\.]/g, '');
      if (cleaned.length > 12) return;
      setFormData(prev => ({ ...prev, phone: value }));
      return;
    }

    // Validar CP mientras escribe (solo dígitos, máx 5)
    if (name === 'postal_code') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 5);
      setFormData(prev => ({ ...prev, postal_code: digitsOnly }));
      
      // Debounce: validar dirección automáticamente 800ms después de terminar de escribir
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        validateAddress(formData.city, digitsOnly, formData.country);
      }, 800);
      return;
    }

    // Si cambia la ciudad, también validar con debounce
    if (name === 'city') {
      setFormData(prev => ({ ...prev, city: value }));
      
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        validateAddress(value, formData.postal_code, formData.country);
      }, 800);
      return;
    }
    
    // Si cambia el país, validar inmediatamente
    if (name === 'country') {
      setFormData(prev => ({ ...prev, country: value }));
      setAddressValid(null);
      if (formData.city.trim() && formData.postal_code.trim().length === 5) {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
          validateAddress(formData.city, formData.postal_code, value);
        }, 500);
      }
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Limpiar el timer al desmontar
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    
    if (!formData.full_name.trim()) {
      errors.full_name = 'Nombre completo es obligatorio';
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Teléfono es obligatorio';
    } else if (!isValidPhone(formData.phone)) {
      errors.phone = 'Teléfono inválido. Debe ser español (9 dígitos, ej: 612345678)';
    }
    if (!formData.address.trim()) {
      errors.address = 'Dirección es obligatoria';
    }
    if (!formData.city.trim()) {
      errors.city = 'Ciudad es obligatoria';
    }
    if (!formData.postal_code.trim()) {
      errors.postal_code = 'Código postal es obligatorio';
    } else if (!isValidPostalCode(formData.postal_code)) {
      errors.postal_code = 'El código postal debe tener 5 dígitos (ej: 28004)';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    
    // Si la dirección no se ha validado aún o es inválida, validar ahora
    if (addressValid !== true) {
      setValidating(true);
      try {
        const validationResponse = await axios.post(
          'https://reanimeitor.com/backend/validate_address.php',
          {
            city: formData.city,
            postal_code: formData.postal_code,
            country: formData.country
          },
          { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
        );
        
        if (!validationResponse.data.success || !validationResponse.data.valid) {
          setAddressValid(false);
          setError(validationResponse.data.message || 'La combinación de ciudad, código postal y país no es válida.');
          setValidating(false);
          return;
        }
        setAddressValid(true);
      } catch (err) {
        setError('Error de conexión. Intenta de nuevo.');
        setValidating(false);
        return;
      }
      setValidating(false);
    }
    
    setLoading(true);
    setError('');
    setFieldErrors({});
    
    try {
      // Guardar dirección
      const response = await axios.post(
        'https://reanimeitor.com/backend/save_shipping_address.php',
        {
          user_id: user.id,
          ...formData
        },
        { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
      );
      
      if (response.data.success) {
        onSuccess(formData);
      } else {
        setError(response.data.error || 'Error al guardar dirección');
      }
    } catch (err) {
      console.error(err);
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="shipping-modal-overlay">
      <div className="shipping-modal">
        <button className="shipping-modal-close" onClick={onCancel}>
          <FaTimes />
        </button>
        <div className="shipping-modal-header">
          <h2>Información de Envío</h2>
          <p>Completa tus datos para recibir tu reanimeitor Genesis Can</p>
        </div>
        
        <form onSubmit={handleSubmit} className="shipping-form">
          <div className="form-group">
            <label><FaUser /> Nombre completo</label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              placeholder="Nombre y apellidos"
              className={fieldErrors.full_name ? 'input-error' : ''}
              required
            />
            {fieldErrors.full_name && <span className="field-error">{fieldErrors.full_name}</span>}
          </div>
          
          <div className="form-group">
            <label><FaPhone /> Teléfono</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="612345678"
              maxLength={12}
              className={fieldErrors.phone ? 'input-error' : ''}
              required
            />
            {fieldErrors.phone && <span className="field-error">{fieldErrors.phone}</span>}
          </div>
          
          <div className="form-group">
            <label><FaMapMarkerAlt /> Dirección</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Calle, número, piso"
              className={fieldErrors.address ? 'input-error' : ''}
              required
            />
            {fieldErrors.address && <span className="field-error">{fieldErrors.address}</span>}
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label><FaCity /> Ciudad</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Ciudad"
                className={fieldErrors.city ? 'input-error' : ''}
                required
              />
              {fieldErrors.city && <span className="field-error">{fieldErrors.city}</span>}
            </div>
            
            <div className="form-group">
              <label><FaEnvelope /> Código Postal</label>
              <input
                type="text"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                placeholder="28004"
                maxLength={5}
                className={fieldErrors.postal_code ? 'input-error' : ''}
                required
              />
              {fieldErrors.postal_code && <span className="field-error">{fieldErrors.postal_code}</span>}
            </div>
          </div>
          
          <div className="form-group">
            <label>País</label>
            <select name="country" value={formData.country} onChange={handleChange}>
              <option value="España">España</option>
              <option value="Andorra">Andorra</option>
              <option value="Francia">Francia</option>
              <option value="Portugal">Portugal</option>
              <option value="Alemania">Alemania</option>
              <option value="Italia">Italia</option>
            </select>
          </div>
          
          {validating && (
            <div className="shipping-validating">
              <FaSpinner className="spinning-icon" /> Verificando dirección...
            </div>
          )}
          {addressValid === true && !error && !validating && (
            <div className="shipping-valid">
              ✅ Dirección verificada correctamente
            </div>
          )}
          {addressValid === false && !error && !validating && (
            <div className="shipping-error">
              ⚠️ Ciudad o código postal no encontrados. ¿Son correctos?
            </div>
          )}
          {error && <div className="shipping-error">{error}</div>}
          
          <div className="shipping-actions">
            <button type="button" className="btn-cancel" onClick={onCancel}>
              Cancelar
            </button>
            <button type="submit" className="btn-submit" disabled={loading || validating || addressValid === false}>
              {loading || validating ? 'Validando...' : (
                <>
                  Continuar al pago <FaArrowRight />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShippingForm;